import { ReactNode, useEffect, useRef } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { useGetAutoLoanProductsQuery } from "@lending/hooks/queries/AutoLoanProduct/useGetAutoLoanProductsQuery";
import { useGetEligibilityCriteriaQuery } from "@lending/hooks/queries/EligibilityCriteria/useGetEligibilityCriteriaQuery";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useFlowNavigation } from "@lending/navigation";
import { isMlaQuestionEnabled } from "@lending/pages/Common/ApplicationQuestions/mlaQuestion";

interface AutoLoanGuardProps {
  children?: ReactNode;
}

function AutoLoanGuard({ children }: AutoLoanGuardProps) {
  const { autoLoanProductsData } = useGetAutoLoanProductsQuery();
  const { hasEligibilityCriteria } = useGetEligibilityCriteriaQuery();
  const { institution } = useGetInstitutionQuery();
  const hasLendingQuestions = isMlaQuestionEnabled(institution.lending_ask_additional_fields);
  const { startFlow } = useFlowNavigation();
  const location = useLocation();
  const hasStartedRef = useRef(false);

  const hasAutoLoanProducts = (autoLoanProductsData?.count ?? 0) > 0;
  const isBarePath = location.pathname === "/auto-loan" || location.pathname === "/auto-loan/";

  // Mirror the product-selection button on the bare product URL: startFlow
  // both clears stale flow state (so useFlowNavigation reads the right
  // productType) and navigates via useNavigateWithSearch (preserving any
  // query params). The ref guards against startFlow's unstable reference
  // re-firing the effect and re-triggering updateData on every render.
  useEffect(() => {
    if (!isBarePath) {
      hasStartedRef.current = false;
      return;
    }
    if (hasStartedRef.current || !hasAutoLoanProducts) return;
    hasStartedRef.current = true;
    startFlow(
      "auto-loan",
      { hasEligibilityCriteria, hasLendingQuestions },
      { navigateOptions: { replace: true } }
    );
  }, [isBarePath, hasAutoLoanProducts, hasEligibilityCriteria, hasLendingQuestions, startFlow]);

  if (!hasAutoLoanProducts) {
    return <Navigate to="/" replace />;
  }

  if (isBarePath) {
    return null;
  }

  return children ? <>{children}</> : <Outlet />;
}

export { AutoLoanGuard };
